import React, { useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import moment from "moment";
import Label from "src/components/Label";
import { getTrackerDetailApi } from "src/DAL/tracker/tracker";
import {
  getInitials,
  getSubmissionReviewLabel,
  isScheduledReviewDetail,
  loadSubmissionDetailFromApi,
  sendSubmissionFeedbackApi,
  SUBMISSION_REVIEW,
  updateSubmissionFromApi,
} from "./submissionStorage";
import { normalizeTracker } from "./trackerStorage";
import CoachReviewPanel from "./CoachReviewPanel";
import { backTarget, withBack } from "./trackerNav";

const PRIMARY = "var(--portal-theme-primary)";
const PRIMARY_SOFT =
  "color-mix(in srgb, var(--portal-theme-primary) 12%, white)";
const BORDER = "#e2e8f0";
const MUTED = "#637381";

const reviewColor = (status) => {
  if (status === SUBMISSION_REVIEW.OVERDUE) return "error";
  if (status === SUBMISSION_REVIEW.PENDING) return "warning";
  if (status === SUBMISSION_REVIEW.REVIEWED) return "success";
  return "default";
};

/**
 * Scheduled full-journey review (internal). Check-in chat/email stays on Submissions.
 */
export default function TrackerReviewDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const back = backTarget(location, navigate, "/tracker-reviews", "Back to reviews");
  const submissionPath = `/tracker-submissions/${id}`;
  // Opened from this submission → back to it (keeps its own Back); else open it with Back here.
  const openSubmission = () =>
    back.to === submissionPath
      ? back.go()
      : navigate(submissionPath, { state: withBack() });
  const { enqueueSnackbar } = useSnackbar();
  const [submission, setSubmission] = useState(null);
  const [tracker, setTracker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const result = await loadSubmissionDetailFromApi(id);
        if (cancelled) return;
        if (!result.ok || !result.submission) {
          enqueueSnackbar(result.message || "Review not found", {
            variant: "error",
          });
          navigate("/tracker-reviews", { replace: true });
          return;
        }
        setSubmission(result.submission);
        if (result.submission.trackerId) {
          const trackerRes = await getTrackerDetailApi(
            result.submission.trackerId
          ).catch(() => null);
          if (!cancelled && trackerRes?.code === 200 && trackerRes.tracker) {
            setTracker(normalizeTracker(trackerRes.tracker));
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [id, enqueueSnackbar, navigate]);

  const handleSubmit = async (patch) => {
    if (!submission?.id || saving) return;
    if (patch?.error) {
      enqueueSnackbar(patch.error, { variant: "warning" });
      return;
    }
    const { memberFeedback, ...metaPatch } = patch;
    setSaving(true);
    const result = await updateSubmissionFromApi(submission.id, metaPatch);
    if (!result.ok) {
      setSaving(false);
      enqueueSnackbar(result.message || "Could not save review", {
        variant: "error",
      });
      return;
    }
    let next = result.submission;
    if (
      memberFeedback?.message &&
      (memberFeedback.sendChat || memberFeedback.sendEmail)
    ) {
      const sent = await sendSubmissionFeedbackApi(submission.id, {
        message: memberFeedback.message,
        sendChat: Boolean(memberFeedback.sendChat),
        sendEmail: Boolean(memberFeedback.sendEmail),
      });
      if (sent.ok && sent.submission) next = sent.submission;
      else if (!sent.ok) {
        enqueueSnackbar(
          sent.message || "Review saved, but message could not be sent",
          { variant: "warning" }
        );
      }
    }
    setSaving(false);
    setSubmission(next);
    const checkIn = !isScheduledReviewDetail(next?.reviewDetail);
    enqueueSnackbar(
      metaPatch.reviewStatus === "reviewed"
        ? checkIn
          ? "Review completed"
          : "Scheduled review completed"
        : "Review updated",
      { variant: "success" }
    );
    // Check-in review: back to the submission it was opened from.
    if (checkIn && metaPatch.reviewStatus === "reviewed") {
      openSubmission();
    }
  };

  if (loading || !submission) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress size={36} />
      </Box>
    );
  }

  const isScheduled = isScheduledReviewDetail(submission.reviewDetail);

  return (
    <div className="container-fluid tracker-module-page">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          mb: 2,
          flexWrap: "wrap",
        }}
      >
        <IconButton
          size="small"
          onClick={back.go}
          sx={{ p: 0.75 }}
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography
          component="button"
          type="button"
          onClick={back.go}
          sx={{
            border: 0,
            background: "none",
            p: 0,
            cursor: "pointer",
            color: MUTED,
            fontSize: 13,
            "&:hover": { color: PRIMARY },
          }}
        >
          {back.label}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <button
          type="button"
          className="small-contained-outlined-button"
          onClick={openSubmission}
        >
          Open submission
        </button>
      </Box>

      <Box
        sx={{
          mb: 2.5,
          p: { xs: 2, sm: 2.25 },
          borderRadius: "12px",
          border: `1px solid ${BORDER}`,
          bgcolor: "#fff",
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Avatar
          sx={{
            width: 52,
            height: 52,
            fontSize: 16,
            fontWeight: 700,
            bgcolor: PRIMARY_SOFT,
            color: PRIMARY,
          }}
        >
          {getInitials(submission.memberName)}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: "1 1 220px" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
              mb: 0.5,
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, lineHeight: 1.3, color: "text.primary" }}
            >
              {submission.memberName}
            </Typography>
            <Label
              variant="ghost"
              color={reviewColor(submission.reviewStatus)}
            >
              {getSubmissionReviewLabel(submission.reviewStatus)}
            </Label>
          </Box>
          {submission.email ? (
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", fontSize: 13 }}
            >
              {submission.email}
            </Typography>
          ) : null}
          <Typography
            variant="body2"
            sx={{ mt: 0.5, color: "text.secondary", fontSize: 13 }}
          >
            {submission.trackerName || "Tracker"}
            {submission.reviewDetail ? ` · ${submission.reviewDetail}` : ""}
            {submission.coachName ? ` · Coach ${submission.coachName}` : ""}
          </Typography>
          {submission.submittedAt ? (
            <Typography sx={{ mt: 0.35, fontSize: 12, color: MUTED }}>
              Linked check-in{" "}
              {moment(submission.submittedAt).format("DD MMM YYYY h:mm A")}
            </Typography>
          ) : null}
        </Box>
      </Box>

      {!isScheduled ? (
        <Alert severity="info" sx={{ mb: 2, borderRadius: "12px" }}>
          Check-in review: answer the questions below, then complete the
          review. Chat/email feedback stays on the{" "}
          <Typography
            component="button"
            type="button"
            onClick={openSubmission}
            sx={{
              border: 0,
              background: "none",
              p: 0,
              color: PRIMARY,
              fontWeight: 700,
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            submission
          </Typography>
          .
        </Alert>
      ) : null}

      <CoachReviewPanel
        tracker={tracker}
        submission={submission}
        saving={saving}
        onSubmit={handleSubmit}
        hideMemberNotify
      />

      {isScheduled ? (
        <Typography sx={{ mt: 1, fontSize: 12.5, color: MUTED }}>
          This is an internal scheduled review for the journey. Member-facing
          chat/email notes stay on the submission.
        </Typography>
      ) : null}
    </div>
  );
}
